import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import api from '../common/api';
import { useGlobalState } from '../state';

// Who created and last edited a purchase / sale / daily entry, shown in the dialog footer. Only the main admin
// of the account sees it. created_by / modified_by hold the user's email; the account's user names are loaded
// once and the email is shown when no name is found (e.g. a removed sub user).
let userNamesRequest = null;
let userNamesAccountID = null;

const getAccountUserNames = (accountID) => {
    if (userNamesRequest === null || userNamesAccountID !== accountID) {
        userNamesAccountID = accountID;
        userNamesRequest = api.get('account-user-names')
            .then(res => {
                const names = {};
                (res || []).forEach(u => { names[u.email] = u.name; });
                return names;
            })
            .catch(() => {
                userNamesRequest = null;
                return {};
            });
    }
    return userNamesRequest;
};

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1.5, 3),
        marginRight: 'auto',
        minWidth: 0,
    },
    // DialogActions outside DialogContent has 8px padding: line up with the 24px dialog content
    indent: {
        marginLeft: theme.spacing(2),
    },
    footer: {
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.5, 3),
        margin: 0,
    },
    person: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
    },
    avatar: {
        width: 30,
        height: 30,
        fontSize: 12,
        fontWeight: 600,
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.action.selected,
    },
    label: {
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    name: {
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    time: {
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
    },
}));

const isValidTime = (value) => value && moment(value).isValid() && moment(value).year() > 1;

const initialsOf = (name) => {
    const parts = name.split(/[\s@._-]+/).filter(Boolean);
    return ((parts[0] || '')[0] || '').concat((parts[1] || '')[0] || '').toUpperCase() || '?';
};

const AuditPerson = ({ classes, action, name, time }) => (
    <div className={classes.person}>
        <Avatar className={classes.avatar}>{initialsOf(name)}</Avatar>
        <div style={{ minWidth: 0 }}>
            <Typography variant="caption" component="div" color="textSecondary" className={classes.label}>
                {action} <span className={classes.name}>{name}</span>
            </Typography>
            {isValidTime(time) ?
                <Tooltip title={moment(time).fromNow()} placement="top">
                    <Typography variant="caption" component="div" color="textSecondary" className={classes.time}>
                        {moment(time).format("D MMM YYYY, h:mm A")}
                    </Typography>
                </Tooltip> : null}
        </div>
    </div>
);

// footer: render as its own strip (for dialogs without action buttons); otherwise it sits on the left of the
// dialog's action buttons. indent=false when those buttons are already inside the padded DialogContent.
const EntryAuditInfo = ({ entry, footer = false, indent = true }) => {
    const classes = useStyles();
    const [{ user }] = useGlobalState();
    const [userNames, setUserNames] = useState({});
    const isMainAdmin = user && user.role === "ADMIN";

    useEffect(() => {
        if (!isMainAdmin) {
            return;
        }
        let active = true;
        getAccountUserNames(user.accountID).then(names => {
            if (active) setUserNames(names);
        });
        return () => { active = false; };
    }, [isMainAdmin, user]);

    if (!isMainAdmin || !entry || !entry.createdBy) {
        return null;
    }

    const nameOf = (email) => userNames[email] || email;
    // create stamps created_* and modified_* with the same user and time, so equal times mean never edited
    const isEdited = isValidTime(entry.modifiedTime) && entry.modifiedBy &&
        moment(entry.modifiedTime).diff(moment(entry.createTime), 'seconds') > 0;

    return (
        <div className={footer ? `${classes.root} ${classes.footer}` : `${classes.root} ${indent ? classes.indent : ''}`}>
            <AuditPerson classes={classes} action="Created by" name={nameOf(entry.createdBy)} time={entry.createTime}/>
            {isEdited ?
                <AuditPerson classes={classes} action="Last edited by" name={nameOf(entry.modifiedBy)} time={entry.modifiedTime}/>
                : null}
        </div>
    );
};

export default EntryAuditInfo;
